import React from 'react';
import { Box, Button, InputAdornment, Slider, Stack, TextField, Typography } from '@mui/material';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import { GlassCard } from '../../FatigueManagement/components/primitives';
import type { RoiInputs } from '../data/model';

interface Props {
  inputs: RoiInputs;
  onChange: (next: RoiInputs) => void;
  onReset: () => void;
}

type NumericKey = Exclude<keyof RoiInputs, 'reductions' | 'attribution'>;
type ReductionKey = keyof RoiInputs['reductions'];

interface FieldDef {
  key: NumericKey;
  label: string;
  adornment?: '£';
  helper?: string;
}

const FIELDS: FieldDef[] = [
  { key: 'headcount', label: 'Employees in scope' },
  { key: 'costPerSickDay', label: 'Cost per sick day', adornment: '£' },
  { key: 'absenceDaysPerEmployee', label: 'Absence days / employee / yr' },
  { key: 'presenteeismDaysPerEmployee', label: 'Presenteeism days / employee / yr' },
  { key: 'productivityValuePerDay', label: 'Output value / day', adornment: '£' },
  { key: 'turnoverLeaversPerYear', label: 'Leavers / year' },
  { key: 'costPerReplacement', label: 'Cost per replacement', adornment: '£' },
  { key: 'injuriesPerYear', label: 'Injuries / year' },
  { key: 'costPerInjury', label: 'Cost per injury', adornment: '£' },
  { key: 'healthcareReferralsPerYear', label: 'Healthcare referrals / year' },
  { key: 'costPerReferral', label: 'Cost per referral', adornment: '£' },
];

const REDUCTIONS: { key: ReductionKey; label: string; max?: number; note?: string }[] = [
  { key: 'absence', label: 'Absence reduction' },
  { key: 'presenteeism', label: 'Presenteeism reduction' },
  {
    key: 'productivity',
    label: 'Productivity uplift',
    max: 10,
    note: 'Capped at 10%: a sustained year-round output uplift above this is not credible, so it stays lower than the other levers.',
  },
  { key: 'injury', label: 'Injury reduction' },
  { key: 'turnover', label: 'Turnover reduction' },
  { key: 'healthcare', label: 'Healthcare reduction' },
];

export const AssumptionsSection: React.FC<Props> = ({ inputs, onChange, onReset }) => {
  const setNumeric = (key: NumericKey, raw: string) => {
    const value = Number(raw);
    onChange({ ...inputs, [key]: Number.isFinite(value) && value >= 0 ? value : 0 });
  };

  const setReduction = (key: ReductionKey, value: number) => {
    onChange({
      ...inputs,
      reductions: { ...inputs.reductions, [key]: value / 100 },
    });
  };

  return (
    <Stack spacing={3}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Box>
          <Typography variant='h5' fontWeight={800}>
            Assumptions
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Tune to your organisation. Defaults are UK benchmarks (see Methodology).
          </Typography>
        </Box>
        <Button variant='outlined' size='small' startIcon={<RestartAltRoundedIcon />} onClick={onReset}>
          Reset defaults
        </Button>
      </Stack>

      <GlassCard>
        <Typography variant='h6' fontWeight={800} sx={{ mb: 2 }}>
          Workforce & cost inputs
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: 2,
          }}
        >
          {FIELDS.map((f) => (
            <TextField
              key={f.key}
              label={f.label}
              type='number'
              size='small'
              value={inputs[f.key]}
              onChange={(e) => setNumeric(f.key, e.target.value)}
              InputProps={
                f.adornment
                  ? {
                      startAdornment: <InputAdornment position='start'>{f.adornment}</InputAdornment>,
                    }
                  : undefined
              }
              inputProps={{ min: 0 }}
            />
          ))}
        </Box>
      </GlassCard>

      <GlassCard>
        <Typography variant='h6' fontWeight={800} sx={{ mb: 1 }}>
          Attribution
        </Typography>
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          Share of the headline opportunity credibly attributable to the programme. The report claims this fraction of
          gross savings: <strong>{Math.round(inputs.attribution * 100)}%</strong>.
        </Typography>
        <Slider
          value={Math.round(inputs.attribution * 100)}
          onChange={(_, v) => onChange({ ...inputs, attribution: (v as number) / 100 })}
          min={0}
          max={100}
          step={1}
          valueLabelDisplay='auto'
          valueLabelFormat={(v) => `${v}%`}
          marks={[
            { value: 10, label: '10%' },
            { value: 20, label: '20%' },
            { value: 50, label: '50%' },
          ]}
          aria-label='Attribution percentage'
        />
      </GlassCard>

      <GlassCard>
        <Typography variant='h6' fontWeight={800} sx={{ mb: 2 }}>
          Targeted improvements
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
          }}
        >
          {REDUCTIONS.map((r) => {
            const pctValue = Math.round(inputs.reductions[r.key] * 100);
            return (
              <Box key={r.key}>
                <Stack direction='row' justifyContent='space-between'>
                  <Typography variant='body2' fontWeight={600}>
                    {r.label}
                  </Typography>
                  <Typography variant='body2' fontWeight={700}>
                    {pctValue}%
                  </Typography>
                </Stack>
                <Slider
                  value={pctValue}
                  onChange={(_, v) => setReduction(r.key, v as number)}
                  min={0}
                  max={r.max ?? 50}
                  step={1}
                  valueLabelDisplay='auto'
                  valueLabelFormat={(v) => `${v}%`}
                  aria-label={r.label}
                />
                {r.note && (
                  <Typography variant='caption' color='text.secondary'>
                    {r.note}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
      </GlassCard>
    </Stack>
  );
};
